// Preferências de acessibilidade (painel "Acessibilidade" do menu da engrenagem).
//
// Regra de ouro: sem preferência gravada, NADA é aplicado. Nenhuma classe,
// variável ou estilo novo entra no <html>; a plataforma fica idêntica ao que
// era antes do painel existir. Cada opção ligada vira uma classe no <html>
// (styles/Acessibilidade.css) e só ela.
//
// O backend tem o espelho destas regras em
// backend/src/services/acessibilidadePolicy.js: mudou uma chave ou um valor
// aqui, mude lá também.

export const CHAVE_ESPELHO = 'exxerAcessibilidade';

export const PREFERENCIAS_PADRAO = Object.freeze({
  fonte: 'normal',
  altoContraste: false,
  semAnimacao: false,
  espacamento: false,
  vlibras: false
});

export const VALORES_FONTE = Object.freeze(['normal', 'grande', 'muito_grande']);
const CHAVES_BOOLEANAS = ['altoContraste', 'semAnimacao', 'espacamento', 'vlibras'];

// Classe de cada opção. Todas começam com "a11y-" para a limpeza saber o que é dela.
export const CLASSES = Object.freeze({
  fonteGrande: 'a11y-fonte-grande',
  fonteMuitoGrande: 'a11y-fonte-muito-grande',
  altoContraste: 'a11y-alto-contraste',
  semAnimacao: 'a11y-sem-animacao',
  espacamento: 'a11y-espacamento'
});
const TODAS_AS_CLASSES = Object.values(CLASSES);

// Aceita qualquer coisa (localStorage, resposta da API) e devolve um objeto
// completo e válido. Valor desconhecido cai no padrão daquela chave.
export const normalizarPreferencias = (entrada) => {
  const base = { ...PREFERENCIAS_PADRAO };
  if (!entrada || typeof entrada !== 'object' || Array.isArray(entrada)) return base;
  if (VALORES_FONTE.includes(entrada.fonte)) base.fonte = entrada.fonte;
  CHAVES_BOOLEANAS.forEach((chave) => {
    if (typeof entrada[chave] === 'boolean') base[chave] = entrada[chave];
  });
  return base;
};

export const ehPadrao = (preferencias) => {
  const p = normalizarPreferencias(preferencias);
  return Object.keys(PREFERENCIAS_PADRAO).every((chave) => p[chave] === PREFERENCIAS_PADRAO[chave]);
};

export const classesDasPreferencias = (preferencias) => {
  const p = normalizarPreferencias(preferencias);
  const classes = [];
  if (p.fonte === 'grande') classes.push(CLASSES.fonteGrande);
  if (p.fonte === 'muito_grande') classes.push(CLASSES.fonteMuitoGrande);
  if (p.altoContraste) classes.push(CLASSES.altoContraste);
  if (p.semAnimacao) classes.push(CLASSES.semAnimacao);
  if (p.espacamento) classes.push(CLASSES.espacamento);
  return classes;
};

// Põe e tira só as classes "a11y-*". O VLibras não tem classe: é o
// componente VLibras quem carrega e remove o widget.
// O tema (data-theme) do alto contraste é do ThemeContext.
export const aplicarNoDocumento = (preferencias, raiz = document.documentElement) => {
  if (!raiz) return;
  const desejadas = classesDasPreferencias(preferencias);
  TODAS_AS_CLASSES.forEach((classe) => {
    if (!desejadas.includes(classe) && raiz.classList.contains(classe)) raiz.classList.remove(classe);
  });
  desejadas.forEach((classe) => {
    if (!raiz.classList.contains(classe)) raiz.classList.add(classe);
  });
  // Um class="" vazio no <html> também seria diferença: some quando não sobra nada.
  if (raiz.hasAttribute('class') && raiz.classList.length === 0) raiz.removeAttribute('class');
};

// Espelho no localStorage: vale antes do login, enquanto a API responde e em
// telas sem sessão (login, recuperação de senha). Padrão = chave apagada.
export const lerEspelho = () => {
  try {
    const texto = window.localStorage.getItem(CHAVE_ESPELHO);
    if (!texto) return { ...PREFERENCIAS_PADRAO };
    return normalizarPreferencias(JSON.parse(texto));
  } catch (_err) {
    return { ...PREFERENCIAS_PADRAO };
  }
};

export const gravarEspelho = (preferencias) => {
  try {
    if (ehPadrao(preferencias)) window.localStorage.removeItem(CHAVE_ESPELHO);
    else window.localStorage.setItem(CHAVE_ESPELHO, JSON.stringify(normalizarPreferencias(preferencias)));
  } catch (_err) { /* sem storage: vale só nesta página */ }
};

// Movimento reduzido: pelo sistema operacional ou pela opção do painel. Para
// animações feitas em JavaScript (letreiro do Andon e afins).
export const movimentoReduzido = () => {
  if (typeof document !== 'undefined' && document.documentElement.classList.contains(CLASSES.semAnimacao)) return true;
  return Boolean(typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
};
